import { useState } from "react";

import { Toggle } from "@/components/toggle";
import { Button } from "@/components/ui/buttons/button";
import { NativeSelect } from "@/components/ui/select/select-native";
import {
	usePlausibilitySettings,
	useUpdatePlausibilitySettings,
} from "@/features/plausibility/use-plausibility-settings";

/** The bus only delivers events at 7 and up, so the minimum starts there. */
const MIN_SEVERITIES = ["7", "8", "9", "10"];

/**
 * The organization's auto-tracking switch: high and critical events start a
 * track by themselves, with Gemini choosing who to follow. Keyed by
 * organization where it's used, so a draft never carries over to another.
 */
export const AutoTrackPanel = () => {
	const settings = usePlausibilitySettings();
	const update = useUpdatePlausibilitySettings();
	const [enabledDraft, setEnabledDraft] = useState<boolean | null>(null);
	const [severityDraft, setSeverityDraft] = useState<null | number>(null);

	if (!settings.data) return null;
	const saved = settings.data.autoTrack;
	const enabled = enabledDraft ?? saved.enabled;
	const minSeverity = severityDraft ?? saved.minSeverity;
	const dirty = enabled !== saved.enabled || minSeverity !== saved.minSeverity;

	return (
		<section
			aria-label="Auto-tracking"
			className="flex flex-wrap items-end gap-4 rounded-lg border border-secondary p-4"
		>
			<div className="flex min-w-64 flex-1 flex-col gap-2">
				<Toggle
					hint="Gemini picks who the event is about from its clips, then the track follows them. Waits for the verifier where it can change alerts. At most 10 an hour per installation."
					isSelected={enabled}
					label="Auto-track high and critical events"
					onChange={setEnabledDraft}
				/>
			</div>
			<NativeSelect
				disabled={!enabled}
				label="Severity at least"
				onChange={(event) => setSeverityDraft(Number(event.target.value))}
				options={MIN_SEVERITIES.map((value) => ({ label: value, value }))}
				value={String(minSeverity)}
			/>
			<div className="flex items-center gap-3">
				{update.isSuccess && !dirty && (
					<span className="text-sm text-secondary">Saved.</span>
				)}
				<Button
					isDisabled={update.isPending || !dirty}
					onClick={() =>
						update.mutate(
							{ autoTrack: { enabled, minSeverity } },
							{
								onSuccess: () => {
									setEnabledDraft(null);
									setSeverityDraft(null);
								},
							},
						)
					}
					size="sm"
				>
					{update.isPending ? "Saving…" : "Save"}
				</Button>
			</div>
			{update.error && (
				<p className="w-full text-sm text-error-primary" role="alert">
					{update.error.message}
				</p>
			)}
		</section>
	);
};
